/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { StyledText } from "../../../assets/typography/typography";
import { colors } from "../../colors";

type Props = {
  label?: string;
  innerRef?: any;
  text?: React.ReactNode;
  wrapperWidth?: string;
} & React.ClassAttributes<HTMLInputElement> &
  React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;

const wrapperStyle = css`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: fit-content;
  align-items: center;
  margin-bottom: 0px;
  > input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
  > input:checked ~ .customElement {
    background-color: ${colors.blueInteractive};
    > .checkedIndicator {
      left: 32px;
    }
    > .offIndicator {
      display: none;
    }
    > .onIndicator {
      display: flex;
    }
  }
  > input:disabled ~ .customElement {
    opacity: 50%;
    > .checkedIndicator {
      background-color: ${colors.ctaInactive};
    }
  }
  > input:focus ~ .customElement {
    outline: 2px solid ${colors.blueMedium};
    outline-offset: 1px;
  }
  &:hover {
    > input:enabled:not(:checked) ~ .customElement {
      background-color: #cdcdcd;
    }
  }
  > .customElement {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    padding: 2px;
    width: 50px;
    height: 20px;
    min-width: 50px;
    min-height: 20px;
    transition: justify-content 0.5s ease;
    background-color: #e2e2e2;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.04) inset,
      0px 2px 4px 0px rgba(0, 0, 0, 0.04) inset,
      0px 0px 0px 0.5px rgba(0, 0, 0, 0.08) inset;
    border-radius: 9999px;
    > .onIndicator {
      display: none;
      position: absolute;
      left: 5px;
      top: 50%;
      text-align: center;
      justify-content: center;
      width: 24px;
      transform: translate(0%, -50%);
    }
    > .offIndicator {
      position: absolute;
      right: 5px;
      top: 50%;
      width: 24px;
      text-align: center;
      justify-content: center;
      transform: translate(0%, -50%);
    }
    > .checkedIndicator {
      background-color: ${colors.white};
      position: absolute;
      transition: left 300ms;
      left: 2px;
      top: 2px;
      z-index: 1000;
      border-radius: 9999px;
      width: 16px;
      height: 16px;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.06),
        0px 0px 0px 0.5px rgba(0, 0, 0, 0.04);
    }
  }
`;

export const SwitchField = ({
  label,
  value,
  innerRef,
  disabled,
  text,
  wrapperWidth,
  ...rest
}: Props) => {
  return (
    <label css={wrapperStyle}>
      <input
        disabled={disabled}
        value={value}
        type="checkbox"
        ref={innerRef}
        {...rest}
      />
      <span className="customElement">
        <div className="checkedIndicator" />
        <StyledText
          color={colors.textSecondary}
          className="bodySubtextRegular mb-0 offIndicator"
        >
          OFF
        </StyledText>
        <StyledText
          color={colors.white}
          className="bodySubtextRegular mb-0 onIndicator"
        >
          ON
        </StyledText>
      </span>
      {label && (
        <StyledText
          color={disabled ? colors.textSecondary : colors.textPrimary}
          className="bodyTextRegular mb-0"
        >
          {label}
        </StyledText>
      )}
    </label>
  );
};
